import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import { SR_API_ACTION_UTILITY_PARTNER_INVITE_TEAM_MEMBERS_BY_EMAIL } from 'api/utilityActionTypes';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import { SR_EDIT_TEXTBOX_INPUT_TYPE_EMAIL } from 'client/components/SrEditTextbox';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';
import UPLoadingConfirmationSpinner from 'client/components/UPLoadingConfirmationSpinner/UPLoadingConfirmationSpinner';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

import './UpSelfOnboarding.less';

const SENDING_INVITE_STATUS_NOT_SENT = 'NOT_SENT';
const SENDING_INVITE_STATUS_SENDING = 'SENDING';
const SENDING_INVITE_STATUS_SENT = 'SENT';

const getFormFields = () => {
  const formFields = [
    {
      id: 'teamMembers',
      title: 'Type their email addresses below',
      subtitle: 'Optional, you can invite team members later.',
      type: 'text_list',
      properties: {
        className: 'selfOnboardingSubtitle',
        placeholder: 'Write email address here...',
        inputType: SR_EDIT_TEXTBOX_INPUT_TYPE_EMAIL,
        withSaveButton: true,
        addButtonProps: {
          theme: SR_BUTTON_THEME_ADOBE_GREEN,
          margin: '10px 0',
          text: 'Add Email',
        },
        textListItemProps: {
          containerStyle: {
            marginTop: '12px',
          },
          itemTitle: 'Invitee #',
          className: 'teamMemberEmail',
          removeButtonChildren: (
            <SrBox
              width="20px"
              height="20px"
              backgroundImage="url(/images/x-circle-icon-red.svg)"
              backgroundSize="cover"
              backgroundRepeat="no-repeat"
              backgroundPosition="center"
            />
          ),
        },
      },
    },
  ];
  return formFields;
};

const UpSelfOnboardingInviteTeamStep = ({
  activeIndex,
  utilityPartner,
  utilityPartnerGroup,
  onNext,
  onNavigateToStep,
}) => {
  const [sendingInviteStatus, setSendingInviteStatus] = useState(SENDING_INVITE_STATUS_NOT_SENT);
  const [errorMessage, setErrorMessage] = useState(null);

  const theme = utilityPartnerGroup?.defaultTheme || {};
  const dispatch = useDispatch();

  const formFields = getFormFields();

  const shouldShowLoadingSpinner =
    sendingInviteStatus === SENDING_INVITE_STATUS_SENDING ||
    sendingInviteStatus === SENDING_INVITE_STATUS_SENT;

  const onSubmit = (formValues) => {
    if (formValues.teamMembers.length === 0) {
      return onNext();
    }

    setErrorMessage(null);
    setSendingInviteStatus(SENDING_INVITE_STATUS_SENDING);

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_INVITE_TEAM_MEMBERS_BY_EMAIL,
      payload: {
        utilityPartnerId: utilityPartner.id,
        teamMemberEmails: formValues.teamMembers,
      },
      onSuccess: () => {
        setSendingInviteStatus(SENDING_INVITE_STATUS_SENT);
        return setTimeout(() => {
          setSendingInviteStatus(SENDING_INVITE_STATUS_NOT_SENT);
          onNext();
        }, 2500);
      },
      onError: (error) => {
        setErrorMessage(error.message);
        setSendingInviteStatus(SENDING_INVITE_STATUS_NOT_SENT);
      },
    });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formValues = formikBag.values;
        const hasTeamMembers = formValues.teamMembers.length > 0;
        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                onNavigateToStep={onNavigateToStep}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form" onSubmit={preventDefaultPropagation}>
                <span styleName="titleText">Invite your team.</span>
                <span styleName="subTitleText">Keep everyone up-to-date. It's free, why not!</span>
                <SrBox margin="26px 0">
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                {shouldShowLoadingSpinner && (
                  <SrBox margin="20px 0">
                    <UPLoadingConfirmationSpinner
                      successText="Invites sent!"
                      successIconUrl="/images/checkmark-circle-green.svg"
                      loadingText="Sending invites..."
                      loadingIconUrl="/images/green-spinner.svg"
                      success={sendingInviteStatus === SENDING_INVITE_STATUS_SENT}
                    />
                  </SrBox>
                )}
                {errorMessage && hasTeamMembers && (
                  <SrErrorMessage margin="20px 0" borderRadius="5px">
                    {errorMessage}
                  </SrErrorMessage>
                )}
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREY}
                  onClick={formikBag.handleSubmit}
                  disabled={shouldShowLoadingSpinner}
                  hasBoxShadow
                  themeOverrides={{
                    backgroundColor: theme?.buttonPrimaryColor,
                    color: theme?.buttonPrimaryTextColor,
                  }}
                >
                  {hasTeamMembers ? 'Send Invites' : 'Continue without inviting'}
                </SrButton>
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content inviteTeamStep fadeIn">
                <div styleName="row">
                  <span styleName="titleText">Your team:</span>
                  <img
                    src="/images/up-self-onboarding-invite-team.png"
                    alt="Utility Activation"
                    styleName="utilityActivationImg"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingInviteTeamStep.displayName = 'UpSelfOnboardingInviteTeamStep';

UpSelfOnboardingInviteTeamStep.propTypes = {
  activeIndex: PropTypes.number,
  utilityPartnerGroup: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onNavigateToStep: PropTypes.func,
};

export default UpSelfOnboardingInviteTeamStep;
